import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import brand from "../../brand.svg";
import cartService from "../../db/cart-service";
import subscriptionService from "../../services/subscription-service";
import UserProfile from "../Profile/UserProfile";
import InstallButton from "../InstallPrompt/InstallButton";
import SubscriptionBanner from "../SubscriptionBanner/SubscriptionBanner";
import freeTrialService from "../../services/free-trial-service";
import "../../App.scss";

export default function Header({ isUserAuthenticated, handleLogout, isAdmin }) {
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === "/login";
  const [cartCount, setCartCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [hasSubscriptions, setHasSubscriptions] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [bannerVisible, setBannerVisible] = useState(false);
  const [isTrialEligible, setIsTrialEligible] = useState(false);
  const [lastScrollY, setLastScrollY] = useState(0);
  const customerId = sessionStorage.getItem("customerId");
  const customerName = sessionStorage.getItem("customerName") || "User";
  
  // Smart header scroll effect
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY < 10) {
        setHeaderVisible(true);
        setBannerVisible(false);
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setHeaderVisible(false);
        setBannerVisible(true);
      } else if (currentScrollY < lastScrollY && currentScrollY > 100) {
        setHeaderVisible(true);
        setBannerVisible(false);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);
  
  // Close menu when clicking outside
  useEffect(() => {
    const closeMenu = (e) => {
      if (menuOpen && !e.target.closest('.nav-links') && !e.target.closest('.hamburger-menu')) {
        setMenuOpen(false);
      }
      if (profileDropdownOpen && !e.target.closest('.profile-dropdown') && !e.target.closest('.profile-icon')) {
        setProfileDropdownOpen(false);
      }
    };
    
    const handleOpenProfile = () => {
      setProfileOpen(true);
    };
    
    document.addEventListener('click', closeMenu);
    window.addEventListener('openProfileModal', handleOpenProfile);
    return () => {
      document.removeEventListener('click', closeMenu);
      window.removeEventListener('openProfileModal', handleOpenProfile);
    };
  }, [menuOpen, profileDropdownOpen]);

  // Check if user has subscriptions - use session cache to avoid _all_docs call
  const checkUserSubscriptions = async (cId, isAuth) => {
    if (cId && isAuth) {
      try {
        const cached = sessionStorage.getItem('hasSubscriptions');
        if (cached !== null) { setHasSubscriptions(cached === 'true'); return; }
        const userHasSubscriptions = await subscriptionService.hasUserSubscriptions(cId);
        sessionStorage.setItem('hasSubscriptions', String(userHasSubscriptions));
        setHasSubscriptions(userHasSubscriptions);
      } catch (error) {
        console.error('Error checking subscriptions:', error);
        setHasSubscriptions(false);
      }
    } else {
      setHasSubscriptions(false);
    }
  };

  // Function to fetch cart count
  const fetchCartCount = async (cId, isAuth) => {
    if (cId && isAuth) {
      try {
        const cart = await cartService.getCart(cId);
        const count = cart.reduce((total, item) => total + item.quantity, 0);
        setCartCount(count);
      } catch (err) {
        console.error("Error fetching cart:", err);
      }
    } else {
      setCartCount(0);
    }
  };

  // Fetch cart count and check subscriptions once on mount / auth change
  useEffect(() => {
    if (isUserAuthenticated && customerId) {
      fetchCartCount(customerId, isUserAuthenticated);
      checkUserSubscriptions(customerId, isUserAuthenticated);
      if (sessionStorage.getItem('trialJustClaimed') === 'true') {
        setIsTrialEligible(false);
      } else {
        freeTrialService.getTrialStatus()
          .then(status => {
            const eligible = status?.eligible === true && status?.claimed !== true;
            setIsTrialEligible(eligible);
            if (status?.claimed === true) sessionStorage.setItem('trialJustClaimed', 'true');
          })
          .catch(() => {});
      }
    } else {
      setIsTrialEligible(false);
    }
  }, [customerId, isUserAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for cart updates and subscription changes via events only
  useEffect(() => {
    const handleCartUpdate = () => fetchCartCount(customerId, isUserAuthenticated);
    const handleSubscriptionUpdate = () => {
      sessionStorage.removeItem('hasSubscriptions');
      checkUserSubscriptions(customerId, isUserAuthenticated);
    };
    const handleOpenProfile = () => handleProfileClick();

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('refreshSubscriptions', handleSubscriptionUpdate);
    window.addEventListener('openProfileModal', handleOpenProfile);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('refreshSubscriptions', handleSubscriptionUpdate);
      window.removeEventListener('openProfileModal', handleOpenProfile);
    };
  }, [isUserAuthenticated, customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleShareApp = async () => {
    const shareData = {
      title: 'Organic Greens Hub',
      text: '🌿 Fresh organic immunity shots & smoothies delivered Mon–Fri! Try the Organic Greens Hub app:',
      url: 'https://organicgreenshub.com'
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        alert('Link copied to clipboard!');
      }
    }
    setMenuOpen(false);
  };

  const handleProfileClick = () => {
    setProfileOpen(true);
    setProfileDropdownOpen(false);
    setMenuOpen(false);
  };

  return (
    <>
      <header className={`app-header ${headerVisible ? 'header-visible' : 'header-hidden'}`}>
        <div className="header-left">
          <Link to="/">
            <img src={brand} alt="Brand Logo" className="brand-logo" />
          </Link>
        </div>
        
        <div className="hamburger-menu" onClick={(e) => {
          e.stopPropagation();
          setMenuOpen(!menuOpen);
        }}>
          <div className="bar"></div>
          <div className="bar"></div>
          <div className="bar"></div>
        </div>
        
        {menuOpen && <div className="menu-overlay active" onClick={() => setMenuOpen(false)}></div>}
        
        <nav className={`nav-links ${menuOpen ? 'active' : ''}`}>
          <Link to="/" onClick={() => setMenuOpen(false)}>Home</Link>
          <Link to="/subscriptions" onClick={() => setMenuOpen(false)}>Subscriptions/Plans</Link>
          <Link to="/plants" onClick={() => setMenuOpen(false)}>Library</Link>
          <Link to="/about" onClick={() => setMenuOpen(false)}>About Us</Link>
          
          {isUserAuthenticated && (
            <>
              <Link to="/orders" onClick={() => setMenuOpen(false)}>My Past Orders</Link>
              <Link to="/cart" className="cart-link" onClick={() => setMenuOpen(false)}>
                Cart {cartCount > 0 && <span className="cart-count">({cartCount})</span>}
              </Link>
            </>
          )}
          
          <InstallButton />

          <a
            href="https://www.eatforhealth.gov.au/nutrition-calculators/daily-nutrient-requirements-calculator"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMenuOpen(false)}
            style={{ background: 'none', border: '1px solid #4CAF50', cursor: 'pointer', padding: '6px 12px', fontSize: '13px', color: '#133c0b', fontWeight: '700', borderRadius: '20px', whiteSpace: 'nowrap', textDecoration: 'none' }}
          >
            🥗 Nutrition Calculator
          </a>
          
          <button
            onClick={handleShareApp}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', fontSize: '14px', color: '#133c0b', fontWeight: 'bold' }}
          >
            🔗 Share
          </button>
          
          {isUserAuthenticated ? (
            <div className="profile-section">
              <div 
                className="profile-icon" 
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileDropdownOpen(!profileDropdownOpen);
                }}
              >
                <div className="profile-avatar">
                  {customerName.charAt(0).toUpperCase()}
                </div>
                <span className="profile-name">{customerName}</span>
              </div>
              
              {profileDropdownOpen && (
                <div className="profile-dropdown">
                  <button onClick={() => {
                    setProfileOpen(true);
                    setProfileDropdownOpen(false);
                    setMenuOpen(false);
                  }}>
                    My Profile
                  </button>
                  <button onClick={handleShareApp}>
                    🔗 Share
                  </button>
                  {hasSubscriptions && isUserAuthenticated && (
                    <button onClick={() => {
                      navigate('/customer');
                      setTimeout(() => {
                        const event = new CustomEvent('setCustomerTab', { detail: 'subscriptions' });
                        window.dispatchEvent(event);
                      }, 100);
                      setProfileDropdownOpen(false);
                      setMenuOpen(false);
                    }}>
                      My Subscriptions
                    </button>
                  )}
                  <button onClick={() => {
                    const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                                  window.navigator.standalone || 
                                  document.referrer.includes('android-app://');
                    
                    if (isPWA) {
                      alert('You are using the PWA app. Your session will remain active. To logout, please uninstall the app.');
                    } else {
                      handleLogout();
                    }
                    setProfileDropdownOpen(false);
                    setMenuOpen(false);
                  }}>
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            !isLoginPage && <Link to="/login" onClick={() => setMenuOpen(false)}>Login</Link>
          )}
        </nav>
      </header>
      
      <UserProfile 
        isOpen={profileOpen} 
        onClose={() => setProfileOpen(false)} 
      />
      {!isAdmin && <SubscriptionBanner visible={bannerVisible} isTrialEligible={isTrialEligible} />}
    </>
  );
}